import React, { useState, useEffect } from "react";
import {
  Box,
  Typography,
  TextField,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Grid,
  Paper,
  IconButton,
  Chip,
  CircularProgress,
  Alert,
  Divider,
  InputAdornment,
  FormHelperText,
  Card,
  CardContent,
  CardMedia,
  CardActions,
  Stack,
  Button,
  Tooltip,
} from "@mui/material";
import {
  ArrowBack as ArrowBackIcon,
  Add as AddIcon,
  Delete as DeleteIcon,
  Upload as UploadIcon,
  Close as CloseIcon,
  Save as SaveIcon,
  Send as SendIcon,
  Message as MessageIcon,
  PictureAsPdf as PdfIcon,
  Dashboard as CardIcon,
  ViewCarousel as CarouselIcon,
  Image as ImageIcon,
  Link as LinkIcon,
  SmartButton as ButtonIcon,
  AspectRatio as AspectRatioIcon,
  Phone as PhoneIcon,
  Language as LanguageIcon,
  OpenInNew as OpenInNewIcon,
  LocationOn as LocationIcon,
  CloudUpload as CloudUploadIcon,
  InfoOutlined as InfoIcon,
  Tune as TuneIcon,
  TextFields as TextFieldsIcon,
  Reply as ReplyIcon,
  Preview as PreviewIcon,
} from "@mui/icons-material";
import { styled } from "@mui/material/styles";
import { RCS_BOT_TEMPLATES, RCS_UPLOAD_IMAGE } from "../../config/urls";
import axios from "axios";
import { useAuth } from "../../hooks/AuthProvider";
import BackButton from "../../common/back-button";

// ── Design System ──────────────────────────────────────────────────────────────
import typography from "../../cssUtils/typography";
import {
  ColorsConstants,
  borderColors,
  dashboardCardBgColor,
  grayFontColor,
  inputColor,
  formConstants,
} from "../../cssUtils/constants";

// ── Shared Components ──────────────────────────────────────────────────────────
import PhoneMockup from "../../common/PhoneMockup";
import ButtonOne   from "../../common/normal-button";
import ButtonTwo   from "../../common/dashboard-button";
import ButtonThree from "../../common/third-button";

// ─────────────────────────────────────────────────────────────────────────────
// Design tokens
// ─────────────────────────────────────────────────────────────────────────────
const INPUT_SX = {
  "& .MuiOutlinedInput-root": {
    borderRadius: "8px",
    backgroundColor: ColorsConstants.primaryWhite,
    color: inputColor,
    ...typography.bodyLarge,
    "& .MuiOutlinedInput-notchedOutline": { borderColor: ColorsConstants.inputFieldGrey },
    "&:hover .MuiOutlinedInput-notchedOutline": { borderColor: ColorsConstants.primaryGray50 },
    "&.Mui-focused .MuiOutlinedInput-notchedOutline": { borderColor: ColorsConstants.primaryBlue100 },
    "&.Mui-error   .MuiOutlinedInput-notchedOutline": { borderColor: ColorsConstants.error },
  },
  "& .MuiInputBase-input": { ...typography.bodyLarge, color: inputColor, padding: "9px 12px" },
  "& .MuiFormHelperText-root": { ...typography.bodySmall, ml: 0, mt: "4px" },
  "& .MuiInputLabel-root": { ...typography.bodyLarge, color: grayFontColor },
};

const MULTILINE_SX = {
  "& .MuiOutlinedInput-root": {
    borderRadius: "8px",
    backgroundColor: ColorsConstants.primaryWhite,
    color: inputColor,
    ...typography.bodyLarge,
    "& .MuiOutlinedInput-notchedOutline": { borderColor: ColorsConstants.inputFieldGrey },
    "&:hover .MuiOutlinedInput-notchedOutline": { borderColor: ColorsConstants.primaryGray50 },
    "&.Mui-focused .MuiOutlinedInput-notchedOutline": { borderColor: ColorsConstants.primaryBlue100 },
    "&.Mui-error   .MuiOutlinedInput-notchedOutline": { borderColor: ColorsConstants.error },
  },
  "& .MuiInputBase-input": { ...typography.bodyLarge, color: inputColor },
  "& .MuiFormHelperText-root": { ...typography.bodySmall, ml: 0, mt: "4px" },
  "& .MuiInputLabel-root": { ...typography.bodyLarge, color: grayFontColor },
};

const SELECT_SX = {
  borderRadius: "8px",
  backgroundColor: ColorsConstants.primaryWhite,
  ...typography.bodyLarge,
  color: inputColor,
  "& .MuiOutlinedInput-notchedOutline": { borderColor: ColorsConstants.inputFieldGrey },
  "&:hover .MuiOutlinedInput-notchedOutline": { borderColor: ColorsConstants.primaryGray50 },
  "&.Mui-focused .MuiOutlinedInput-notchedOutline": { borderColor: ColorsConstants.primaryBlue100 },
  "& .MuiSelect-select": { ...typography.bodyLarge, color: inputColor, padding: "9px 12px" },
};

const CARD_SX = {
  backgroundColor: ColorsConstants.primaryWhite,
  border: `1px solid ${borderColors.cardBorderPrimaryGrayColor}`,
  borderRadius: "12px",
};

const SECTION_HEADER_SX = {
  backgroundColor: dashboardCardBgColor,
  border: `1px solid ${borderColors.cardBorderPrimaryGrayColor}`,
  borderRadius: "10px",
  px: 2,
  py: 1.5,
  mb: 2,
  display: "flex",
  alignItems: "center",
  gap: 1,
};

// ─────────────────────────────────────────────────────────────────────────────
// Styled components
// ─────────────────────────────────────────────────────────────────────────────
const UploadArea = styled("div")(({ theme, haserror }) => ({
  border: `2px dashed ${haserror ? theme.palette.error.main : theme.palette.divider}`,
  borderRadius: "8px",
  padding: theme.spacing(3),
  textAlign: "center",
  cursor: "pointer",
  transition: "border-color 0.2s, background-color 0.2s",
  backgroundColor: theme.palette.background.default,
  "&:hover": {
    borderColor: haserror ? theme.palette.error.main : theme.palette.primary.main,
    backgroundColor: theme.palette.action.hover,
  },
}));

const PreviewCard = styled(Card)(({ theme }) => ({
  height: "100%",
  border: `1px solid ${theme.palette.divider}`,
  backgroundColor: "#f8f9fa",
  position: "relative",
}));

// ─────────────────────────────────────────────────────────────────────────────
// Template type config
// ─────────────────────────────────────────────────────────────────────────────
const templateTypeConfigs = {
  text_message:            { label: "Text Message",    icon: <MessageIcon />,  color: "#1976d2", fields: ["text_message_content"] },
  text_message_with_pdf:   { label: "Text + PDF",      icon: <PdfIcon />,      color: "#d32f2f", fields: ["text_message_content", "document_url", "media_file"] },
  rich_card:               { label: "Rich Card",       icon: <CardIcon />,     color: "#388e3c", fields: ["card_title", "card_description", "orientation", "height", "alignment", "document_url", "media_file"] },
  carousel:                { label: "Carousel",        icon: <CarouselIcon />, color: "#7b1fa2", fields: ["carousel_list", "orientation", "width"] },
  text_message_with_media: { label: "Media Template",  icon: <ImageIcon />,    color: "#f57c00", fields: ["text_message_content", "message_order", "document_url", "media_file"] },
};

// ─────────────────────────────────────────────────────────────────────────────
// Primitive UI helpers
// ─────────────────────────────────────────────────────────────────────────────
function SectionHeader({ icon: Icon, title, iconColor }) {
  return (
    <Box sx={SECTION_HEADER_SX}>
      {Icon && <Icon sx={{ color: iconColor || ColorsConstants.primaryBlue100, fontSize: 18 }} />}
      <Typography sx={{ ...typography.bodyLarge, fontWeight: 700, color: ColorsConstants.primaryBlue100 }}>{title}</Typography>
    </Box>
  );
}

function FieldHint({ children }) {
  return <Typography sx={{ ...typography.bodySmall, color: grayFontColor, mb: 1, lineHeight: 1.6 }}>{children}</Typography>;
}

function SectionRule() {
  return <Divider sx={{ borderColor: borderColors.cardBorderPrimaryGrayColor, my: 3 }} />;
}

function CharCount({ current, max }) {
  const over = current > max;
  return (
    <Typography sx={{ ...typography.bodySmall, color: over ? ColorsConstants.error : grayFontColor }}>
      {current}/{max}
    </Typography>
  );
}

function DSSelect({ label, name, value, onChange, children, error, size = "medium", ...rest }) {
  return (
    <FormControl fullWidth size={size} error={error}>
      <InputLabel sx={{ ...typography.bodyLarge, color: grayFontColor }}>{label}</InputLabel>
      <Select
        name={name}
        value={value}
        onChange={onChange}
        label={label}
        sx={{ ...SELECT_SX, height: size === "small" ? 36 : 40 }}
        {...rest}
      >
        {children}
      </Select>
    </FormControl>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// MAIN COMPONENT
// ─────────────────────────────────────────────────────────────────────────────
const RCSCreateTemplate = ({ bot, template, onBack, onSuccess, onError, onNavigate }) => {
  const isEditMode = !!template;
  const { getValidToken } = useAuth();
  const [activeTemplateId, setActiveTemplateId] = useState(template?.id || null);
  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState({});
  const [mediaFile, setMediaFile] = useState(null);
  const [mediaPreview, setMediaPreview] = useState(template?.media_file || null);

  // 🟢 NEW: Extract Region and Brand Name for Indian TRAI rules
  const isIndia = bot?.region === "INDIA";
  const brandName = bot?.brand_details?.brand_name || bot?.brand_name || "";

  const [formData, setFormData] = useState({
    name:                  template?.name || "",
    bot:                   bot?.id || "",
    template_type:         template?.template_type || "text_message",
    template_use_case:     template?.template_use_case || "Promotional",
    text_message_content:  template?.text_message_content || "",
    fallback_text:         template?.fallback_text || "",
    document_url:          template?.document_url || "",
    message_order:         template?.message_order || "text_message_at_top",
    orientation:           template?.orientation || "VERTICAL",
    height:                template?.height || "MEDIUM_HEIGHT",
    width:                 template?.width || "MEDIUM_WIDTH",
    alignment:             template?.alignment || "LEFT",
    card_title:            template?.card_title || "",
    card_description:      template?.card_description || "",
    suggestions:           template?.suggestions || [],
    carousel_list:         template?.carousel_list || [],
    status:                template?.status || "DRAFT",
  });


  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
    if (errors[name]) setErrors((prev) => ({ ...prev, [name]: null }));
    if (name === "document_url" && errors.media) setErrors((prev) => ({ ...prev, media: null }));
  };

  const handleFileChange = (event) => {
    const file = event.target.files[0];
    if (!file) return;
    const isVideo = file.type.startsWith("video/");
    const maxBytes = isVideo ? 10 * 1024 * 1024 : 2 * 1024 * 1024;
    if (file.type !== "application/pdf" && file.size > maxBytes) {
      setErrors((prev) => ({ ...prev, media: `File too large. Max size for standalone ${isVideo ? "videos is 10MB" : "images is 2MB"}.` }));
      setMediaFile(null); setMediaPreview(null); event.target.value = null; return;
    }
    if (file.type === "application/pdf") { setMediaFile(file); if (errors.media) setErrors((prev) => ({ ...prev, media: null })); return; }
    const validateDimensions = (width, height) => {
      // Aspect ratio validation temporarily disabled — investigating dotgo-side rejection
      // const ratio = width / height; const tolerance = 0.05; let isValid = true; let errorMessage = "";
      // if (formData.template_type === "text_message_with_media") {
      //   const allowedRatios = [{ name: "1:1", value: 1.0 }, { name: "16:9", value: 16 / 9 }, { name: "4:3", value: 4 / 3 }, { name: "9:16", value: 9 / 16 }];
      //   isValid = allowedRatios.some((r) => Math.abs(ratio - r.value) <= tolerance);
      //   if (!isValid) errorMessage = `Invalid ratio (${ratio.toFixed(2)}). Standard formats required (1:1, 16:9, 4:3, 9:16).`;
      // } else if (formData.template_type === "rich_card") {
      //   let expectedRatio = 0; let ratioName = "";
      //   if (formData.orientation === "HORIZONTAL") { expectedRatio = 3 / 4; ratioName = "3:4 (Portrait)"; }
      //   else if (formData.orientation === "VERTICAL") {
      //     if (formData.height === "SHORT_HEIGHT")  { expectedRatio = 3.0;   ratioName = "3:1 (Ultra Wide Banner)"; }
      //     else if (formData.height === "MEDIUM_HEIGHT") { expectedRatio = 2.0; ratioName = "2:1 (Landscape)"; }
      //     else if (formData.height === "TALL_HEIGHT")   { expectedRatio = 4 / 5; ratioName = "4:5 (Tall)"; }
      //   }
      //   if (Math.abs(ratio - expectedRatio) > tolerance) { isValid = false; errorMessage = `Invalid aspect ratio (${ratio.toFixed(2)}). Dotgo requires a ${ratioName} image for this layout.`; }
      // }
      // if (!isValid) { setErrors((prev) => ({ ...prev, media: errorMessage })); setMediaFile(null); setMediaPreview(null); event.target.value = null; }
      // else { ... }
      setMediaFile(file);
      if (errors.media) setErrors((prev) => ({ ...prev, media: null }));
      setMediaPreview(URL.createObjectURL(file));
    };
    if (isVideo) {
      const video = document.createElement("video"); video.preload = "metadata";
      video.onloadedmetadata = () => { window.URL.revokeObjectURL(video.src); validateDimensions(video.videoWidth, video.videoHeight); };
      video.src = URL.createObjectURL(file);
    } else {
      const img = new Image();
      img.onload = () => { window.URL.revokeObjectURL(img.src); validateDimensions(img.width, img.height); };
      img.src = URL.createObjectURL(file);
    }
  };

  const removeFile = () => { setMediaFile(null); setMediaPreview(null); };

  const addSuggestion = () => {
    setFormData((prev) => ({ ...prev, suggestions: [...prev.suggestions, { suggestionType: "reply", displayText: "", postback: "empty_postback", text: "", action_type: "reply" }] }));
  };

  const updateSuggestion = (index, field, value) => {
    const updatedSuggestions = [...formData.suggestions];
    const fieldMappings = { text: "displayText", action_type: "suggestionType", postback_data: "postback", url: "url", phoneNumber: "phoneNumber", application: "application" };
    const backendField = fieldMappings[field] || field;
    updatedSuggestions[index][field] = value;
    if (backendField !== field) updatedSuggestions[index][backendField] = value;
    if (field === "action_type") {
      if (value === "url_action") updatedSuggestions[index].url = "";
      else if (value === "dialer_action") updatedSuggestions[index].phoneNumber = "";
      updatedSuggestions[index].suggestionType = value;
    }
    setFormData((prev) => ({ ...prev, suggestions: updatedSuggestions }));
  };

  const removeSuggestion = (index) => { setFormData((prev) => ({ ...prev, suggestions: prev.suggestions.filter((_, i) => i !== index) })); };

  const addCarouselCard = () => { setFormData((prev) => ({ ...prev, carousel_list: [...prev.carousel_list, { title: "", description: "", media_url: "", suggestions: [] }] })); };

  const updateCarouselCard = (index, field, value) => {
    const updatedCards = [...formData.carousel_list]; updatedCards[index][field] = value;
    setFormData((prev) => ({ ...prev, carousel_list: updatedCards }));
  };

  const removeCarouselCard = (index) => { setFormData((prev) => ({ ...prev, carousel_list: prev.carousel_list.filter((_, i) => i !== index) })); };

  const addCardSuggestion = (cardIndex) => {
    const updatedCards = [...formData.carousel_list];
    updatedCards[cardIndex].suggestions.push({ type: "action", text: "", action_type: "url_action", url: "" });
    setFormData((prev) => ({ ...prev, carousel_list: updatedCards }));
  };

  const updateCardSuggestion = (cardIndex, suggestionIndex, field, value) => {
    const updatedCards = [...formData.carousel_list]; updatedCards[cardIndex].suggestions[suggestionIndex][field] = value;
    setFormData((prev) => ({ ...prev, carousel_list: updatedCards }));
  };

  const removeCardSuggestion = (cardIndex, suggestionIndex) => {
    const updatedCards = [...formData.carousel_list];
    updatedCards[cardIndex].suggestions = updatedCards[cardIndex].suggestions.filter((_, i) => i !== suggestionIndex);
    setFormData((prev) => ({ ...prev, carousel_list: updatedCards }));
  };

  const handleCarouselFileUpload = async (event, cardIndex) => {
    const file = event.target.files[0]; if (!file) return;
    const isVideo = file.type.startsWith("video/");
    const maxBytes = isVideo ? 5 * 1024 * 1024 : 1 * 1024 * 1024;
    if (file.size > maxBytes) { setErrors((prev) => ({ ...prev, [`card_${cardIndex}_media`]: `File too large. Max size for carousel ${isVideo ? "videos is 5MB" : "images is 1MB"}.` })); event.target.value = null; return; }
    const validateCarouselDimensions = (width, height) => {
      // Aspect ratio validation temporarily disabled — investigating dotgo-side rejection
      // const ratio = width / height; const tolerance = 0.05; let isValid = true; let expectedRatio = 0; let ratioName = "";
      // if (formData.width === "SMALL_WIDTH"  && formData.height === "SHORT_HEIGHT")  { expectedRatio = 5 / 4; ratioName = "5:4"; }
      // else if (formData.width === "MEDIUM_WIDTH" && formData.height === "SHORT_HEIGHT")  { expectedRatio = 2.0;   ratioName = "2:1"; }
      // else if (formData.width === "SMALL_WIDTH"  && formData.height === "MEDIUM_HEIGHT") { expectedRatio = 4 / 5; ratioName = "4:5"; }
      // else if (formData.width === "MEDIUM_WIDTH" && formData.height === "MEDIUM_HEIGHT") { expectedRatio = 4 / 3; ratioName = "4:3"; }
      // else { setErrors(...); return false; }
      // if (isValid && Math.abs(ratio - expectedRatio) > tolerance) { setErrors(...); return false; }
      if (errors[`card_${cardIndex}_media`]) setErrors((prev) => ({ ...prev, [`card_${cardIndex}_media`]: null }));
      return true;
    };
    if (isVideo) {
      const video = document.createElement("video"); video.preload = "metadata";
      video.onloadedmetadata = () => { window.URL.revokeObjectURL(video.src); if (validateCarouselDimensions(video.videoWidth, video.videoHeight)) { executeCarouselUpload(file, cardIndex, event); } else { event.target.value = null; } };
      video.src = URL.createObjectURL(file);
    } else {
      const img = new Image();
      img.onload = () => { window.URL.revokeObjectURL(img.src); if (validateCarouselDimensions(img.width, img.height)) { executeCarouselUpload(file, cardIndex, event); } else { event.target.value = null; } };
      img.src = URL.createObjectURL(file);
    }
  };

  const executeCarouselUpload = async (file, cardIndex, event) => {
    const uploadData = new FormData(); uploadData.append("file", file);
    const originalText = formData.carousel_list[cardIndex].media_url;
    updateCarouselCard(cardIndex, "media_url", "Uploading...");
    try {
      const response = await axios.post(RCS_UPLOAD_IMAGE, uploadData, { headers: { "Content-Type": "multipart/form-data", "X-Tenant-ID": localStorage.getItem("tenant") || "" } });
      if (response.data.url) { updateCarouselCard(cardIndex, "media_url", response.data.url); if (errors[`card_${cardIndex}_media`]) setErrors((prev) => ({ ...prev, [`card_${cardIndex}_media`]: null })); }
    } catch (error) {
      console.error("Upload failed", error);
      setErrors((prev) => ({ ...prev, [`card_${cardIndex}_media`]: "Failed to upload file to server. Please try again." }));
      updateCarouselCard(cardIndex, "media_url", originalText || ""); event.target.value = null;
    }
  };

  const validateName = (name) => {
    if (!name.trim()) return "Template name is required";
    if (name.length > 20) return "Template name must be 20 characters or less";
    if (!/^\w+$/.test(name)) return "Only alphanumeric characters and underscores allowed";
    return null;
  };

  const validateUrl = (url) => {
    if (!url) return null;
    try { new URL(url); return null; } catch { return "Must be a valid URL"; }
  };

  // 🟢 FIX: UNIFIED VALIDATION FUNCTION
  // Evaluates the entire form state at once to prevent React state overwriting
  const validateForm = () => {
    const newErrors = {};

    // 1. Basic Information
    const nameError = validateName(formData.name);
    if (nameError) newErrors.name = nameError;

    // 2. Template Content
    if (formData.template_type === "text_message" || formData.template_type === "text_message_with_pdf") {
      if (!formData.text_message_content.trim()) newErrors.text_message_content = "Message content is required";
      else if (formData.text_message_content.length > 2500) newErrors.text_message_content = "Message content must be 2500 characters or less";
    }
    if (formData.template_type === "rich_card") {
      if (!formData.card_title.trim()) newErrors.card_title = "Card title is required";
      else if (formData.card_title.length > 200) newErrors.card_title = "Card title must be 200 characters or less";
      if (!formData.card_description.trim()) newErrors.card_description = "Card description is required";
      else if (formData.card_description.length > 2000) newErrors.card_description = "Card description must be 2000 characters or less";
      if (!formData.document_url && !mediaFile) newErrors.media = "Either Media File or Document URL is required for Rich Cards";
      if (formData.orientation === "VERTICAL" && !formData.height) newErrors.height = "Height is required for Vertical orientation";
      if (formData.orientation === "HORIZONTAL" && !formData.alignment) newErrors.alignment = "Alignment is required for Horizontal orientation";
    }
    if (formData.template_type === "carousel") {
      if (formData.carousel_list.length < 2) newErrors.carousel_list = "Carousel must have at least 2 cards.";
      else if (formData.carousel_list.length > 10) newErrors.carousel_list = "Carousel cannot exceed 10 cards.";
      formData.carousel_list.forEach((card, index) => {
        if (!card.title?.trim()) newErrors[`card_${index}_title`] = "Title is required";
        if (!card.media_url?.trim()) newErrors[`card_${index}_media`] = "Media URL is required";
        if (card.suggestions.length > 4) newErrors[`card_${index}_suggestions`] = "Max 4 buttons per card";
        card.suggestions.forEach((sug, sIndex) => {
          if (!sug.text?.trim()) newErrors[`card_${index}_sug_${sIndex}_text`] = "Required";
          if (sug.action_type === "url_action" && !sug.url) newErrors[`card_${index}_sug_${sIndex}_url`] = "Required";
          if (sug.action_type === "dialer_action" && !sug.phoneNumber) newErrors[`card_${index}_sug_${sIndex}_phone`] = "Required";
          if (sug.action_type === "view_location_latlong") {
            if (!sug.latitude) newErrors[`card_${index}_sug_${sIndex}_lat`] = "Required";
            else if (sug.latitude < -90 || sug.latitude > 90) newErrors[`card_${index}_sug_${sIndex}_lat`] = "Invalid Lat (-90 to 90)";
            if (!sug.longitude) newErrors[`card_${index}_sug_${sIndex}_lng`] = "Required";
            else if (sug.longitude < -180 || sug.longitude > 180) newErrors[`card_${index}_sug_${sIndex}_lng`] = "Invalid Lng (-180 to 180)";
          }
        });
      });
    }
    // 🟢 NEW: TRAI Validation for Indian Bots
    if (isIndia && brandName) {
      const brandLower = brandName.toLowerCase();
      let hasBrand = false;

      if (["text_message", "text_message_with_pdf", "text_message_with_media"].includes(formData.template_type)) {
        hasBrand = formData.text_message_content.toLowerCase().includes(brandLower);
        if (!hasBrand) newErrors.text_message_content = `TRAI rules: Brand name ("${brandName}") must be included in the message content.`;
      } else if (formData.template_type === "rich_card") {
        const combinedText = `${formData.card_title} ${formData.card_description}`.toLowerCase();
        hasBrand = combinedText.includes(brandLower);
        if (!hasBrand) newErrors.card_description = `TRAI rules: Brand name ("${brandName}") must be included in the title or description.`;
      } else if (formData.template_type === "carousel") {
        const combinedText = formData.carousel_list.map(c => `${c.title} ${c.description}`).join(" ").toLowerCase();
        hasBrand = combinedText.includes(brandLower);
        if (!hasBrand) newErrors.carousel_list = `TRAI rules: Brand name ("${brandName}") must be included in at least one card's title or description.`;
      }
    }
    
    if (formData.document_url) { 
      const urlError = validateUrl(formData.document_url); 
      if (urlError) newErrors.document_url = urlError; 
    }
    
    if (formData.card_description && formData.card_description.length > 2000) {
      newErrors.card_description = "Description must be 2000 characters or less";
    }

    // 3. Action Buttons & Suggestions
    if (formData.template_type !== "carousel") {
      if (formData.template_type === "rich_card" && formData.suggestions.length > 4) {
        newErrors.suggestions = "Rich Cards allow maximum 4 suggestions";
      }
      formData.suggestions.forEach((suggestion, index) => {
        const displayText = suggestion.displayText || suggestion.text;
        if (!displayText || !displayText.trim()) newErrors[`suggestion_${index}`] = "Button text is required";
        if (displayText && displayText.length > 25) newErrors[`suggestion_${index}`] = "Button text must be 25 characters or less";
        if (suggestion.action_type === "view_location_latlong") {
          if (!suggestion.latitude) newErrors[`suggestion_${index}_lat`] = "Latitude required";
          else if (suggestion.latitude < -90 || suggestion.latitude > 90) newErrors[`suggestion_${index}_lat`] = "Must be between -90 and 90";
          if (!suggestion.longitude) newErrors[`suggestion_${index}_lng`] = "Longitude required";
          else if (suggestion.longitude < -180 || suggestion.longitude > 180) newErrors[`suggestion_${index}_lng`] = "Must be between -180 and 180";
        }
        if (suggestion.action_type === "view_location_query" && !suggestion.query?.trim()) {
          newErrors[`suggestion_${index}_query`] = "Search query is required";
        }
      });
    }

    setErrors(newErrors);
    
    const isValid = Object.keys(newErrors).length === 0;
    // 🟢 FIX: Trigger the error toast if validation fails
    if (!isValid && onError) {
      onError("Please fix the highlighted errors in the form before submitting.");
    }
    return isValid;
  };


  const handleSave = async (status = "DRAFT") => {
    // 🟢 FIX: Unified form validation
    if (!validateForm()) return;
    
    setLoading(true);
    try {
      const submitData = new FormData();
      const botId = formData.bot || bot.id;

      submitData.append("bot", botId);

      const fieldsToSend = { ...formData, status: status, bot: botId };

      Object.keys(fieldsToSend).forEach((key) => {
        if (key === "suggestions" || key === "carousel_list") { if (fieldsToSend[key] && fieldsToSend[key].length > 0) submitData.append(key, JSON.stringify(fieldsToSend[key])); }
        else if (fieldsToSend[key] !== null && fieldsToSend[key] !== undefined && fieldsToSend[key] !== "") submitData.append(key, fieldsToSend[key]);
      });

      if (mediaFile) submitData.append("media_file", mediaFile);

      console.log("Submitting template:", { template_type: fieldsToSend.template_type, bot: fieldsToSend.bot, orientation: fieldsToSend.orientation, height: fieldsToSend.height, alignment: fieldsToSend.alignment, suggestionCount: fieldsToSend.suggestions?.length || 0 });
      
      const url = activeTemplateId ? `${RCS_BOT_TEMPLATES.replace("{botId}", bot.id)}${activeTemplateId}/` : RCS_BOT_TEMPLATES.replace("{botId}", bot.id);
      
      const method = activeTemplateId ? "put" : "post";

      const response = await axios({ method, url, data: submitData, headers: { "Content-Type": "multipart/form-data", "X-Tenant-ID": localStorage.getItem("tenant") || "", Authorization: `Bearer ${await getValidToken()}` } });
      if (response.data && response.data.partial_success) { onError(response.data.error); if (response.data.template && response.data.template.id) setActiveTemplateId(response.data.template.id); return; }
      
      onSuccess(response.data);
      
      // 🟢 FIX: Your exact working redirection code
      if (typeof onNavigate === "function") {
        onNavigate("view_templates", {
          activeTab: 1,     // force RCS tab
          selectedBot: bot, // pass bot
        });
      } else {
        console.warn("onNavigate prop is missing! Falling back to onBack().");
        onBack();
      }
      
    } catch (error) {

      console.error("Error saving template:", error);
      let errorMessage = "Failed to save template. Please try again.";
      const errData = error.response?.data;
      if (errData) {
        if (Array.isArray(errData) && errData.length > 0) errorMessage = errData[0];
        else if (errData.message) errorMessage = errData.message;
        else if (errData.detail) errorMessage = errData.detail;
        else if (errData.error) errorMessage = typeof errData.error === "string" ? errData.error : JSON.stringify(errData.error);
        else if (typeof errData === "object") { const firstKey = Object.keys(errData)[0]; if (firstKey) { const firstVal = errData[firstKey]; errorMessage = `${firstKey}: ${Array.isArray(firstVal) ? firstVal[0] : firstVal}`; } }
        const fieldErrors = {};
        Object.entries(errData).forEach(([field, messages]) => { if (Array.isArray(messages)) fieldErrors[field] = messages[0]; else fieldErrors[field] = String(messages); });
        setErrors(fieldErrors);
      }
      onError(errorMessage);
    } finally { setLoading(false); }
  };

  const getSuggestionIcon = (actionType) => {
    switch (actionType) {
      case "url_action": return <LanguageIcon sx={{ fontSize: 11 }} />;
      case "dial_action": return <PhoneIcon sx={{ fontSize: 11 }} />;
      case "view_location_latlong": return <LocationIcon sx={{ fontSize: 11 }} />;
      case "view_location_query": return <LocationIcon sx={{ fontSize: 11 }} />;
      case "reply": return <ReplyIcon sx={{ fontSize: 11 }} />;
      default: return <OpenInNewIcon sx={{ fontSize: 11 }} />;
    }
  };

  const renderTemplatePreview = () => {
    const getMediaHeight = (heightSetting, widthSetting = "MEDIUM_WIDTH") => {
      if (widthSetting === "MEDIUM_WIDTH" && heightSetting === "SHORT_HEIGHT") return 130;
      if (widthSetting === "SMALL_WIDTH" && heightSetting === "MEDIUM_HEIGHT") return 175;
      if (widthSetting === "MEDIUM_WIDTH" && heightSetting === "MEDIUM_HEIGHT") return 195;
      if (heightSetting === "SHORT_HEIGHT") return 120;
      if (heightSetting === "TALL_HEIGHT") return 220;
      return 160;
    };

    const renderMedia = (customUrl = null, customHeight = null) => {
      const urlToUse = customUrl || mediaPreview || formData.document_url;
      const heightToUse = customHeight || getMediaHeight(formData.height, formData.width);
      if (!urlToUse && !mediaFile) {
        return (
          <Box
            sx={{
              height: heightToUse,
              background: "linear-gradient(135deg, #e0e0e0, #f5f5f5)",
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              justifyContent: "center",
              color: "#aaa",
              borderRadius: "10px 10px 0 0",
            }}
          >
            <ImageIcon sx={{ fontSize: 28, color: "#bbb", mb: 0.5 }} />
            <Typography sx={{ fontSize: "9px", color: "#bbb" }}>Media Preview</Typography>
          </Box>
        );
      }
      if (formData.template_type === "text_message_with_pdf") {
        return (
          <Box
            sx={{
              p: 1.5,
              background: "linear-gradient(135deg, #ffebee, #fce4ec)",
              display: "flex",
              alignItems: "center",
              gap: 1,
              borderRadius: "10px 10px 0 0",
            }}
          >
            <PdfIcon sx={{ color: "#c62828", fontSize: 24 }} />
            <Box>
              <Typography sx={{ fontSize: "11px", fontWeight: 700, color: "#333" }}>{mediaFile?.name || "document.pdf"}</Typography>
              <Typography sx={{ fontSize: "9px", color: "#888" }}>PDF Document</Typography>
            </Box>
          </Box>
        );
      }
      return (
        <CardMedia
          component={mediaFile?.type?.startsWith("video/") ? "video" : "img"}
          height={heightToUse}
          image={urlToUse}
          controls={mediaFile?.type?.startsWith("video/")}
          sx={{ objectFit: "cover", borderRadius: "10px 10px 0 0", display: "block" }}
        />
      );
    };

    const renderSuggestions = (suggestionsList, isVertical = false) => {
      if (!suggestionsList || suggestionsList.length === 0) return null;
      return (
        <Box
          sx={{
            display: "flex",
            flexDirection: isVertical ? "column" : "row",
            flexWrap: isVertical ? "nowrap" : "wrap",
            gap: "5px",
            mt: 1,
          }}
        >
          {suggestionsList.map((sug, idx) => (
            <Box
              key={idx}
              sx={{
                display: "inline-flex",
                alignItems: "center",
                gap: "4px",
                px: 1.25,
                py: "4px",
                border: "1.5px solid #128c7e",
                borderRadius: "20px",
                bgcolor: "rgba(255,255,255,0.9)",
                backdropFilter: "blur(4px)",
                cursor: "pointer",
                width: isVertical ? "100%" : "auto",
                justifyContent: isVertical ? "center" : "flex-start",
                boxShadow: "0 1px 3px rgba(0,0,0,0.08)",
                "&:hover": { bgcolor: "rgba(18,140,126,0.08)" },
              }}
            >
              <Box sx={{ color: "#128c7e", display: "flex", alignItems: "center" }}>
                {getSuggestionIcon(sug.action_type || sug.suggestionType)}
              </Box>
              <Typography sx={{ fontSize: "10px", fontWeight: 600, color: "#128c7e", lineHeight: 1.2, whiteSpace: isVertical ? "nowrap" : "normal" }}>
                {sug.text || sug.displayText || `Action ${idx + 1}`}
              </Typography>
            </Box>
          ))}
        </Box>
      );
    };

    if (formData.template_type === "carousel") {
      const phoneInnerWidth = 258;
      const cardWidth = formData.width === "SMALL_WIDTH" ? 120 : 200;
      const imageHeight = getMediaHeight(formData.height, formData.width);
      return (
        <Box sx={{ width: "100%", overflowX: "auto", pb: 1, "&::-webkit-scrollbar": { height: 3 }, "&::-webkit-scrollbar-thumb": { bgcolor: "rgba(0,0,0,0.15)", borderRadius: 2 } }}>
          <Box sx={{ display: "flex", gap: "8px", width: "max-content" }}>
            {formData.carousel_list.length === 0 ? (
              <Box sx={{ width: phoneInnerWidth, borderRadius: "10px", overflow: "hidden", bgcolor: "#fff", boxShadow: "0 2px 8px rgba(0,0,0,0.12)" }}>
                <Box sx={{ height: 80, bgcolor: "#f0f0f0", display: "flex", alignItems: "center", justifyContent: "center" }}>
                  <CarouselIcon sx={{ color: "#ccc", fontSize: 28 }} />
                </Box>
                <Box sx={{ p: 1 }}>
                  <Typography sx={{ fontSize: "10px", color: "#bbb", textAlign: "center" }}>Add cards to preview</Typography>
                </Box>
              </Box>
            ) : (
              formData.carousel_list.map((card, i) => (
                <Box key={i} sx={{ width: cardWidth, flexShrink: 0, borderRadius: "10px", overflow: "hidden", bgcolor: "#fff", boxShadow: "0 2px 10px rgba(0,0,0,0.14)", display: "flex", flexDirection: "column" }}>
                  <Box sx={{ height: imageHeight, position: "relative", flexShrink: 0 }}>
                    {card.media_url && card.media_url !== "Uploading..." ? (
                      <Box component="img" src={card.media_url} alt={card.title} sx={{ width: "100%", height: "100%", objectFit: "cover", display: "block" }} />
                    ) : (
                      <Box sx={{ width: "100%", height: "100%", background: "linear-gradient(135deg, #e8e8e8, #f5f5f5)", display: "flex", alignItems: "center", justifyContent: "center" }}>
                        {card.media_url === "Uploading..." ? (
                          <CircularProgress size={16} sx={{ color: "#128c7e" }} />
                        ) : (
                          <ImageIcon sx={{ color: "#ccc", fontSize: 22 }} />
                        )}
                      </Box>
                    )}
                  </Box>
                  <Box sx={{ p: 1, flex: 1 }}>
                    <Typography sx={{ fontSize: "11px", fontWeight: 700, color: "#1a1a1a", lineHeight: 1.3, mb: 0.25, display: "-webkit-box", WebkitLineClamp: 2, overflow: "hidden", WebkitBoxOrient: "vertical" }}>
                      {card.title || "Card Title"}
                    </Typography>
                    <Typography sx={{ fontSize: "9px", color: "#888", lineHeight: 1.4, display: "-webkit-box", WebkitLineClamp: 3, overflow: "hidden", WebkitBoxOrient: "vertical" }}>
                      {card.description || "Description…"}
                    </Typography>
                  </Box>
                  {card.suggestions?.length > 0 && (
                    <Box sx={{ px: 1, pb: 1 }}>
                      <Divider sx={{ borderColor: "#f0f0f0", mb: 0.75 }} />
                      {renderSuggestions(card.suggestions, true)}
                    </Box>
                  )}
                </Box>
              ))
            )}
          </Box>
        </Box>
      );
    }

    if (formData.template_type === "rich_card") {
      const isHorizontal = formData.orientation === "HORIZONTAL";
      return (
        <Box
          sx={{
            borderRadius: "12px",
            overflow: "hidden",
            bgcolor: "#fff",
            boxShadow: "0 3px 12px rgba(0,0,0,0.15)",
            display: "flex",
            flexDirection: isHorizontal ? (formData.alignment === "RIGHT" ? "row-reverse" : "row") : "column",
            width: "100%",
          }}
        >
          <Box sx={{ width: isHorizontal ? "45%" : "100%", flexShrink: 0 }}>
            {renderMedia(null, isHorizontal ? 130 : undefined)}
          </Box>
          <Box sx={{ flex: 1, p: 1.25, display: "flex", flexDirection: "column", justifyContent: "space-between" }}>
            <Box>
              <Typography sx={{ fontSize: "12px", fontWeight: 700, color: "#1a1a1a", lineHeight: 1.3, mb: 0.5 }}>
                {formData.card_title || "Card Title"}
              </Typography>
              <Typography sx={{ fontSize: "10px", color: "#666", lineHeight: 1.5, display: "-webkit-box", WebkitLineClamp: 4, overflow: "hidden", WebkitBoxOrient: "vertical" }}>
                {formData.card_description || "Card description will appear here…"}
              </Typography>
            </Box>
            {formData.suggestions?.length > 0 && (
              <Box sx={{ mt: 1 }}>
                <Divider sx={{ borderColor: "#f0f0f0", mb: 0.75 }} />
                {renderSuggestions(formData.suggestions, true)}
              </Box>
            )}
          </Box>
        </Box>
      );
    }

    return (
      <>
        {formData.message_order === "media_at_top" && formData.template_type !== "text_message" && (
          <Box sx={{ borderRadius: "12px", overflow: "hidden", boxShadow: "0 2px 8px rgba(0,0,0,0.12)", mb: 0.5, width: "100%" }}>
            {renderMedia()}
          </Box>
        )}

        {(formData.text_message_content || formData.template_type === "text_message") && (
          <Box sx={{ alignSelf: "flex-start", maxWidth: "90%" }}>
            <Box
              sx={{
                bgcolor: "#fff",
                borderRadius: "4px 16px 16px 16px",
                px: 1.5,
                py: 1,
                boxShadow: "0 1px 4px rgba(0,0,0,0.12), 0 0 0 0.5px rgba(0,0,0,0.04)",
                position: "relative",
              }}
            >
              <Typography
                sx={{
                  fontSize: "12px",
                  color: "#1a1a1a",
                  lineHeight: 1.6,
                  whiteSpace: "pre-wrap",
                  wordBreak: "break-word",
                }}
              >
                {formData.text_message_content || "Your message text will appear here…"}
              </Typography>
              <Box sx={{ display: "flex", justifyContent: "flex-end", mt: 0.25 }}>
                <Typography sx={{ fontSize: "8px", color: "#999" }}>9:41 AM</Typography>
              </Box>
            </Box>
          </Box>
        )}

        {formData.message_order !== "media_at_top" && formData.template_type !== "text_message" && (
          <Box sx={{ borderRadius: "12px", overflow: "hidden", boxShadow: "0 2px 8px rgba(0,0,0,0.12)", mt: 0.5, width: "100%" }}>
            {renderMedia()}
          </Box>
        )}

        {formData.suggestions?.length > 0 && (
          <Box sx={{ alignSelf: "flex-start", width: "100%" }}>
            {renderSuggestions(formData.suggestions, false)}
          </Box>
        )}
      </>
    );
  };

  const currentConfig = templateTypeConfigs[formData.template_type];
  const isCard = ["rich_card", "carousel"].includes(formData.template_type);
  const maxSuggestions = isCard ? 4 : 11;

  const renderActionFields = (suggestion, index, updateFn) => {
    if (suggestion.action_type === "url_action") {
      return (
        <TextField fullWidth size="small" label="Website URL" placeholder="https://example.com" value={suggestion.url || ""}
          onChange={(e) => updateFn(index, "url", e.target.value)} sx={INPUT_SX} />
      );
    }
    if (suggestion.action_type === "dialer_action") {
      return (
        <TextField fullWidth size="small" label="Phone Number" placeholder="+919876543210" value={suggestion.phoneNumber || ""}
          onChange={(e) => updateFn(index, "phoneNumber", e.target.value)} sx={INPUT_SX} />
      );
    }
    if (suggestion.action_type === "view_location_latlong") {
      return (
        <Grid container spacing={1}>
          <Grid item xs={6}>
            <TextField fullWidth size="small" label="Latitude" type="number" value={suggestion.latitude || ""}
              onChange={(e) => updateFn(index, "latitude", parseFloat(e.target.value))}
              error={!!errors[`suggestion_${index}_lat`]} helperText={errors[`suggestion_${index}_lat`]} sx={INPUT_SX} />
          </Grid>
          <Grid item xs={6}>
            <TextField fullWidth size="small" label="Longitude" type="number" value={suggestion.longitude || ""}
              onChange={(e) => updateFn(index, "longitude", parseFloat(e.target.value))}
              error={!!errors[`suggestion_${index}_lng`]} helperText={errors[`suggestion_${index}_lng`]} sx={INPUT_SX} />
          </Grid>
          <Grid item xs={12}>
            <TextField fullWidth size="small" label="Location Label" placeholder="e.g. Headquarters" value={suggestion.label || ""}
              onChange={(e) => updateFn(index, "label", e.target.value)} sx={INPUT_SX} />
          </Grid>
        </Grid>
      );
    }
    if (suggestion.action_type === "view_location_query") {
      return (
        <TextField fullWidth size="small" label="Search Query" placeholder="Coffee shops near me" value={suggestion.query || ""}
          onChange={(e) => updateFn(index, "query", e.target.value)}
          error={!!errors[`suggestion_${index}_query`]} helperText={errors[`suggestion_${index}_query`]} sx={INPUT_SX} />
      );
    }
    if (suggestion.action_type === "calendar_event") {
      return (
        <Stack spacing={1.5} sx={{ p: 1.5, backgroundColor: ColorsConstants.primaryBlue60, borderRadius: "8px" }}>
          <TextField fullWidth size="small" label="Event Title" value={suggestion.title || ""} onChange={(e) => updateFn(index, "title", e.target.value)} sx={INPUT_SX} />
          <TextField fullWidth size="small" multiline rows={2} label="Description" value={suggestion.description || ""} onChange={(e) => updateFn(index, "description", e.target.value)} sx={MULTILINE_SX} />
          <Stack direction="row" spacing={1}>
            <TextField fullWidth size="small" label="Start" type="datetime-local" InputLabelProps={{ shrink: true }} value={suggestion.start_time || ""} onChange={(e) => updateFn(index, "start_time", e.target.value)} sx={INPUT_SX} />
            <TextField fullWidth size="small" label="End" type="datetime-local" InputLabelProps={{ shrink: true }} value={suggestion.end_time || ""} onChange={(e) => updateFn(index, "end_time", e.target.value)} sx={INPUT_SX} />
          </Stack>
        </Stack>
      );
    }
    if (suggestion.action_type === "share_location") {
      return <Alert severity="info" sx={{ py: 0, ...typography.bodySmall }}>User will be prompted to share their live location with the bot.</Alert>;
    }
    return null;
  };

  // ─────────────────────────────────────────────────────────────────────────
  // RENDER
  // ─────────────────────────────────────────────────────────────────────────
  return (
    <Box sx={{ backgroundColor: ColorsConstants.primaryBlue60, minHeight: "100vh", fontFamily: "'Lexend', 'Roboto', sans-serif" }}>

      {/* ── Page Header ─────────────────────────────────────────────────── */}
      <Box sx={{ backgroundColor: ColorsConstants.primaryWhite, borderBottom: `1px solid ${borderColors.cardBorderPrimaryGrayColor}`, px: 4, py: 1.5 }}>
        <Typography sx={{ ...typography.h1, color: ColorsConstants.primaryBlue100 }}>
          {isEditMode ? "Edit Template" : "Create New Template"}
        </Typography>
      </Box>

      {/* ── Body ────────────────────────────────────────────────────────── */}
      <Box sx={{ maxWidth: 1440, mx: "auto", px: 3, py: 3, pb: "90px" }}>

        <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 3 }}>
          <Stack spacing={0.5}>
            <BackButton onClick={onBack} />
            <Typography sx={{ ...typography.bodySmall, color: grayFontColor, ml: 0.5 }}>
              {isEditMode ? `Updating template for ${bot.bot_name}` : `Creating a new template for ${bot.bot_name}`}
            </Typography>
          </Stack>
        </Stack>

        <Grid container spacing={3} alignItems="flex-start">
          <Grid item xs={12} lg={8}>
            <Stack spacing={3}>

              {/* ── Section 1: Basic Information ─────────────────────── */}
              <Box sx={{ ...CARD_SX, p: 3 }}>
                <SectionHeader icon={TextFieldsIcon} title="Basic Information" />

                <Grid container spacing={2.5}>
                  <Grid item xs={12} md={5}>
                    <Typography sx={{ ...typography.bodySmall, fontWeight: 600, color: inputColor, mb: 0.75 }}>
                      Template Name <Box component="span" sx={{ color: ColorsConstants.error }}>*</Box>
                    </Typography>
                    <FieldHint>Alphanumeric and underscores only (e.g., welcome_msg_1). Max 20 characters.</FieldHint>
                    <TextField
                      fullWidth size="small" name="name" value={formData.name}
                      onChange={handleChange}
                      error={!!errors.name}
                      helperText={errors.name}
                      placeholder="e.g. promo_launch_01"
                      InputProps={{ endAdornment: <InputAdornment position="end"><CharCount current={formData.name.length} max={20} /></InputAdornment> }}
                      sx={INPUT_SX}
                    />
                  </Grid>

                  <Grid item xs={12} md={4}>
                    <Typography sx={{ ...typography.bodySmall, fontWeight: 600, color: inputColor, mb: 0.75 }}>
                      Template Type <Box component="span" sx={{ color: ColorsConstants.error }}>*</Box>
                    </Typography>
                    <FieldHint>Select the format for this message template.</FieldHint>
                    <DSSelect label="Template Type" name="template_type" value={formData.template_type} onChange={handleChange} size="small">
                      <MenuItem value="text_message"><Stack direction="row" alignItems="center" spacing={1}><MessageIcon fontSize="small" /><span>Text Message</span></Stack></MenuItem>
                      <MenuItem value="text_message_with_pdf"><Stack direction="row" alignItems="center" spacing={1}><PdfIcon fontSize="small" /><span>Text + PDF</span></Stack></MenuItem>
                      <MenuItem value="rich_card"><Stack direction="row" alignItems="center" spacing={1}><CardIcon fontSize="small" /><span>Rich Card</span></Stack></MenuItem>
                      <MenuItem value="carousel"><Stack direction="row" alignItems="center" spacing={1}><CarouselIcon fontSize="small" /><span>Carousel</span></Stack></MenuItem>
                      <MenuItem value="text_message_with_media"><Stack direction="row" alignItems="center" spacing={1}><ImageIcon fontSize="small" /><span>Media Template</span></Stack></MenuItem>
                    </DSSelect>
                  </Grid>

                  <Grid item xs={12} md={3}>
                    <Typography sx={{ ...typography.bodySmall, fontWeight: 600, color: inputColor, mb: 0.75 }}>Use Case</Typography>
                    <FieldHint>Purpose category for this template.</FieldHint>
                    <DSSelect label="Use Case" name="template_use_case" value={formData.template_use_case} onChange={handleChange} size="small">
                      <MenuItem value="Transactional">Transactional</MenuItem>
                      <MenuItem value="Promotional">Promotional</MenuItem>
                      <MenuItem value="OTP">OTP</MenuItem>
                    </DSSelect>
                  </Grid>

                  <Grid item xs={12}>
                    <Typography sx={{ ...typography.bodySmall, fontWeight: 600, color: inputColor, mb: 0.75 }}>Fallback Text (SMS)</Typography>
                    <FieldHint>Shown on non-RCS devices as a plain SMS. Max 160 characters.</FieldHint>
                    <TextField
                      fullWidth multiline rows={2} name="fallback_text" value={formData.fallback_text}
                      onChange={handleChange} error={!!errors.fallback_text} helperText={errors.fallback_text}
                      placeholder="Fallback message for non-RCS devices..."
                      InputProps={{ endAdornment: <InputAdornment position="end"><CharCount current={formData.fallback_text.length} max={160} /></InputAdornment> }}
                      sx={MULTILINE_SX}
                    />
                  </Grid>
                </Grid>
              </Box>

              {/* ── Section 2: Template Content ──────────────────────── */}
              <Box sx={{ ...CARD_SX, p: 3 }}>
                <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", mb: 2 }}>
                  <Box sx={SECTION_HEADER_SX}>
                    <TuneIcon sx={{ color: ColorsConstants.primaryBlue100, fontSize: 18 }} />
                    <Typography sx={{ ...typography.bodyLarge, fontWeight: 700, color: ColorsConstants.primaryBlue100 }}>Template Content</Typography>
                  </Box>
                  <Chip
                    icon={currentConfig.icon}
                    label={currentConfig.label}
                    size="small"
                    sx={{ ...typography.bodySmall, fontWeight: 600, bgcolor: `${currentConfig.color}15`, color: currentConfig.color, ml: 1 }}
                  />
                </Box>

                {/* 🟢 NEW: TRAI Warning Alert */}
                {isIndia && brandName && (
                  <Alert severity="info" sx={{ mb: 3, ...typography.bodySmall }}>
                   Your brand name <strong>"{brandName}"</strong> must be explicitly mentioned somewhere in the message body.
                  </Alert>
                )}

                {currentConfig.fields.includes("text_message_content") && (
                  <Box sx={{ mb: 3 }}>
                    <Typography sx={{ ...typography.bodySmall, fontWeight: 600, color: inputColor, mb: 0.75 }}>
                      Message Content <Box component="span" sx={{ color: ColorsConstants.error }}>*</Box>
                    </Typography>
                    <TextField
                      fullWidth multiline rows={4} name="text_message_content" value={formData.text_message_content}
                      onChange={handleChange} error={!!errors.text_message_content} helperText={errors.text_message_content}
                      placeholder="Enter your message here..."
                      InputProps={{ endAdornment: <InputAdornment position="end"><CharCount current={formData.text_message_content.length} max={2500} /></InputAdornment> }}
                      sx={MULTILINE_SX}
                    />
                  </Box>
                )}

                {formData.template_type === "text_message_with_media" && (
                  <Box sx={{ backgroundColor: dashboardCardBgColor, border: `1px solid ${borderColors.cardBorderPrimaryGrayColor}`, borderRadius: "8px", p: 2, mb: 3 }}>
                    <Typography sx={{ ...typography.bodyLarge, fontWeight: 600, color: ColorsConstants.primaryBlue100, mb: 1.5 }}>Media Layout Settings</Typography>
                    <Grid container spacing={2}>
                      <Grid item xs={12} sm={6}>
                        <DSSelect label="Message Order" name="message_order" value={formData.message_order || "text_message_at_top"} onChange={handleChange} size="small">
                          <MenuItem value="text_message_at_top">Text at Top (Media Bottom)</MenuItem>
                          <MenuItem value="media_at_top">Media at Top (Text Bottom)</MenuItem>
                        </DSSelect>
                      </Grid>
                      <Grid item xs={12} sm={6}>
                        <DSSelect label="Media Height" name="height" value={formData.height || "MEDIUM_HEIGHT"} onChange={handleChange} size="small">
                          <MenuItem value="SHORT_HEIGHT">Short</MenuItem>
                          <MenuItem value="MEDIUM_HEIGHT">Medium</MenuItem>
                          <MenuItem value="TALL_HEIGHT">Tall</MenuItem>
                        </DSSelect>
                      </Grid>
                    </Grid>
                  </Box>
                )}

                {currentConfig.fields.includes("card_title") && (
                  <Grid container spacing={2.5} sx={{ mb: 3 }}>
                    <Grid item xs={12}>
                      <Typography sx={{ ...typography.bodySmall, fontWeight: 600, color: inputColor, mb: 0.75 }}>
                        Card Title <Box component="span" sx={{ color: ColorsConstants.error }}>*</Box>
                      </Typography>
                      <TextField
                        fullWidth name="card_title" value={formData.card_title} onChange={handleChange}
                        error={!!errors.card_title} helperText={errors.card_title} placeholder="Enter card title"
                        InputProps={{ endAdornment: <InputAdornment position="end"><CharCount current={formData.card_title.length} max={200} /></InputAdornment> }}
                        sx={INPUT_SX}
                      />
                    </Grid>
                    <Grid item xs={12}>
                      <Typography sx={{ ...typography.bodySmall, fontWeight: 600, color: inputColor, mb: 0.75 }}>
                        Card Description <Box component="span" sx={{ color: ColorsConstants.error }}>*</Box>
                      </Typography>
                      <TextField
                        fullWidth multiline rows={3} name="card_description" value={formData.card_description}
                        onChange={handleChange} error={!!errors.card_description} helperText={errors.card_description}
                        placeholder="Enter card description..."
                        InputProps={{ endAdornment: <InputAdornment position="end"><CharCount current={formData.card_description.length} max={2000} /></InputAdornment> }}
                        sx={MULTILINE_SX}
                      />
                    </Grid>
                  </Grid>
                )}

                {formData.template_type === "rich_card" && (
                  <Box sx={{ backgroundColor: dashboardCardBgColor, border: `1px solid ${borderColors.cardBorderPrimaryGrayColor}`, borderRadius: "8px", p: 2, mb: 3 }}>
                    <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1.5 }}>
                      <AspectRatioIcon sx={{ color: ColorsConstants.primaryBlue100, fontSize: 18 }} />
                      <Typography sx={{ ...typography.bodyLarge, fontWeight: 700, color: ColorsConstants.primaryBlue100 }}>Layout Options</Typography>
                    </Stack>
                    <Grid container spacing={2}>
                      <Grid item xs={12} sm={4}>
                        <DSSelect label="Orientation *" name="orientation" value={formData.orientation} onChange={handleChange} error={!!errors.orientation} size="small">
                          <MenuItem value="VERTICAL">Vertical</MenuItem>
                          <MenuItem value="HORIZONTAL">Horizontal</MenuItem>
                        </DSSelect>
                      </Grid>
                      {formData.orientation === "VERTICAL" && (
                        <Grid item xs={12} sm={4}>
                          <DSSelect label="Height *" name="height" value={formData.height} onChange={handleChange} error={!!errors.height} size="small">
                            <MenuItem value="SHORT_HEIGHT">Short Height</MenuItem>
                            <MenuItem value="MEDIUM_HEIGHT">Medium Height</MenuItem>
                          </DSSelect>
                        </Grid>
                      )}
                      {formData.orientation === "HORIZONTAL" && (
                        <Grid item xs={12} sm={4}>
                          <DSSelect label="Alignment *" name="alignment" value={formData.alignment} onChange={handleChange} error={!!errors.alignment} size="small">
                            <MenuItem value="LEFT">Left</MenuItem>
                            <MenuItem value="RIGHT">Right</MenuItem>
                          </DSSelect>
                        </Grid>
                      )}
                    </Grid>
                  </Box>
                )}

                {(currentConfig.fields.includes("document_url") || currentConfig.fields.includes("media_file")) && (
                  <Box sx={{ backgroundColor: dashboardCardBgColor, border: `1px solid ${borderColors.cardBorderPrimaryGrayColor}`, borderRadius: "8px", p: 2.5, mb: 3 }}>
                    <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1.5 }}>
                      <CloudUploadIcon sx={{ color: ColorsConstants.primaryBlue100, fontSize: 18 }} />
                      <Typography sx={{ ...typography.bodyLarge, fontWeight: 700, color: ColorsConstants.primaryBlue100 }}>
                        {formData.template_type === "text_message_with_pdf" ? "PDF Document" : "Media Content"} <Box component="span" sx={{ color: ColorsConstants.error }}>*</Box>
                      </Typography>
                    </Stack>
                    <TextField
                      fullWidth size="small" name="document_url" value={formData.document_url} onChange={handleChange}
                      error={!!errors.document_url} helperText={errors.document_url || "Enter a direct media URL"}
                      placeholder="https://example.com/image.jpg"
                      sx={{ ...INPUT_SX, mb: 2 }}
                    />
                    <Divider sx={{ borderColor: borderColors.cardBorderPrimaryGrayColor, mb: 2 }}>
                      <Typography sx={{ ...typography.bodySmall, color: grayFontColor, px: 1 }}>OR upload a file</Typography>
                    </Divider>
                    <input type="file" accept={formData.template_type === "text_message_with_pdf" ? "application/pdf" : "image/*,video/*"} onChange={handleFileChange} style={{ display: "none" }} id="media-upload" />
                    {mediaPreview || mediaFile ? (
                      <Box sx={{ position: "relative", textAlign: "center", p: 2, border: `1px solid ${borderColors.cardBorderPrimaryGrayColor}`, borderRadius: "8px", backgroundColor: ColorsConstants.primaryWhite }}>
                        <Typography sx={{ ...typography.bodySmall, color: formConstants.greenButton, fontWeight: 600 }}>{mediaFile ? mediaFile.name : "File Selected"}</Typography>
                        <IconButton size="small" onClick={removeFile} sx={{ position: "absolute", top: 4, right: 4 }}><CloseIcon fontSize="small" /></IconButton>
                      </Box>
                    ) : (
                      <Box>
                        <label htmlFor="media-upload">
                          <UploadArea haserror={!!errors.media}>
                            <CloudUploadIcon sx={{ fontSize: 40, color: errors.media ? "error.main" : grayFontColor, mb: 1 }} />
                            <Typography sx={{ ...typography.bodyLarge, color: errors.media ? "error.main" : grayFontColor }}>Click or drag to upload file</Typography>
                          </UploadArea>
                        </label>
                        {errors.media && <FormHelperText error sx={{ textAlign: "center", mt: 1, ...typography.bodySmall }}>{errors.media}</FormHelperText>}
                      </Box>
                    )}
                  </Box>
                )}

                {currentConfig.fields.includes("carousel_list") && (
                  <Box>
                    <Box sx={{ backgroundColor: dashboardCardBgColor, border: `1px solid ${borderColors.cardBorderPrimaryGrayColor}`, borderRadius: "8px", p: 2, mb: 3 }}>
                      <Typography sx={{ ...typography.bodyLarge, fontWeight: 700, color: ColorsConstants.primaryBlue100, mb: 1.5 }}>Carousel Settings</Typography>
                      <Stack direction="row" spacing={2}>
                        <DSSelect label="Card Width" value={formData.width || "MEDIUM_WIDTH"}
                          onChange={(e) => setFormData((prev) => ({ ...prev, width: e.target.value }))}
                          size="small" sx={{ minWidth: 180 }}>
                          <MenuItem value="MEDIUM_WIDTH">Medium (Standard)</MenuItem>
                          <MenuItem value="SMALL_WIDTH">Small (Scroll Hint)</MenuItem>
                        </DSSelect>
                        <DSSelect label="Height" value={formData.height || "MEDIUM_HEIGHT"}
                          onChange={(e) => setFormData((prev) => ({ ...prev, height: e.target.value }))}
                          size="small" sx={{ minWidth: 160 }}>
                          <MenuItem value="SHORT_HEIGHT">Short (Banner)</MenuItem>
                          <MenuItem value="MEDIUM_HEIGHT">Medium (Poster)</MenuItem>
                        </DSSelect>
                      </Stack>
                    </Box>

                    <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 2 }}>
                      <Typography sx={{ ...typography.h2, color: ColorsConstants.primaryBlue100 }}>
                        Carousel Cards ({formData.carousel_list.length}/10)
                      </Typography>
                      <ButtonTwo startIcon={<AddIcon sx={{ fontSize: "15px !important" }} />} onClick={addCarouselCard} disabled={formData.carousel_list.length >= 10} sx={{ height: 34, ...typography.bodySmall }}>
                        Add Card
                      </ButtonTwo>
                    </Stack>
                    {errors.carousel_list && <Alert severity="error" sx={{ mb: 2, ...typography.bodySmall }}>{errors.carousel_list}</Alert>}

                    <Stack spacing={2}>
                      {formData.carousel_list.map((card, cardIndex) => (
                        <Box key={cardIndex} sx={{ ...CARD_SX, border: `1px solid ${borderColors.cardBorderPrimaryGrayColor}`, borderLeft: `4px solid ${ColorsConstants.primaryPurple100}`, p: 2.5, position: "relative" }}>
                          <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 2 }}>
                            <Chip label={`Card ${cardIndex + 1}`} size="small"
                              sx={{ ...typography.bodySmall, fontWeight: 700, backgroundColor: ColorsConstants.primaryPurple100, color: ColorsConstants.primaryWhite, borderRadius: "8px" }} />
                            <IconButton size="small" onClick={() => removeCarouselCard(cardIndex)} disabled={formData.carousel_list.length <= 1}
                              sx={{ color: ColorsConstants.error, border: `1px solid ${ColorsConstants.error}20`, borderRadius: "6px" }}>
                              <DeleteIcon fontSize="small" />
                            </IconButton>
                          </Stack>
                          <Grid container spacing={2}>
                            <Grid item xs={12} md={6}>
                              <Stack spacing={1.5}>
                                <TextField fullWidth size="small" label="Title" value={card.title}
                                  onChange={(e) => updateCarouselCard(cardIndex, "title", e.target.value)}
                                  error={!!errors[`card_${cardIndex}_title`]} helperText={errors[`card_${cardIndex}_title`]} sx={INPUT_SX} />
                                <TextField fullWidth size="small" multiline rows={2} label="Description" value={card.description}
                                  onChange={(e) => updateCarouselCard(cardIndex, "description", e.target.value)} sx={MULTILINE_SX} />
                                <TextField fullWidth size="small" label="Media URL *" value={card.media_url}
                                  onChange={(e) => updateCarouselCard(cardIndex, "media_url", e.target.value)}
                                  placeholder="https://example.com/image.jpg"
                                  error={!!errors[`card_${cardIndex}_media`]} helperText={errors[`card_${cardIndex}_media`]}
                                  sx={INPUT_SX}
                                  InputProps={{
                                    startAdornment: <InputAdornment position="start"><ImageIcon fontSize="small" /></InputAdornment>,
                                    endAdornment: (
                                      <InputAdornment position="end">
                                        <input accept="image/*,video/*" style={{ display: "none" }} id={`carousel-upload-${cardIndex}`} type="file" onChange={(e) => handleCarouselFileUpload(e, cardIndex)} />
                                        <label htmlFor={`carousel-upload-${cardIndex}`}>
                                          <IconButton size="small" component="span" color="primary" title="Upload Image"><UploadIcon /></IconButton>
                                        </label>
                                      </InputAdornment>
                                    ),
                                  }}
                                />
                              </Stack>
                            </Grid>
                            <Grid item xs={12} md={6}>
                              <Box sx={{ backgroundColor: dashboardCardBgColor, border: `1px solid ${borderColors.cardBorderPrimaryGrayColor}`, borderRadius: "8px", p: 2 }}>
                                <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 1.5 }}>
                                  <Typography sx={{ ...typography.bodySmall, fontWeight: 700, color: ColorsConstants.primaryBlue100 }}>
                                    Action Buttons ({card.suggestions.length}/4)
                                  </Typography>
                                  <ButtonThree onClick={() => addCardSuggestion(cardIndex)} disabled={card.suggestions.length >= 4}
                                    startIcon={<AddIcon sx={{ fontSize: "14px !important" }} />}
                                    sx={{ height: 28, ...typography.bodySmall, fontSize: "11px !important" }}>
                                    Add
                                  </ButtonThree>
                                </Stack>
                                <Stack spacing={1.5}>
                                  {card.suggestions.map((suggestion, sIndex) => (
                                    <Box key={sIndex} sx={{ p: 1.5, backgroundColor: ColorsConstants.primaryWhite, border: `1px solid ${borderColors.cardBorderPrimaryGrayColor}`, borderLeft: `3px solid ${ColorsConstants.primaryPurple100}`, borderRadius: "6px" }}>
                                      <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1 }}>
                                        <FormControl size="small" fullWidth>
                                          <Select value={suggestion.action_type || "reply"} onChange={(e) => updateCardSuggestion(cardIndex, sIndex, "action_type", e.target.value)}
                                            displayEmpty sx={{ ...SELECT_SX, height: 32, fontSize: "12px" }}>
                                            <MenuItem value="reply">Reply (Postback)</MenuItem>
                                            <MenuItem value="url_action">Open URL</MenuItem>
                                            <MenuItem value="dialer_action">Dial Phone</MenuItem>
                                            <MenuItem value="view_location_latlong">View Location (Pin)</MenuItem>
                                            <MenuItem value="view_location_query">Search Location</MenuItem>
                                            <MenuItem value="share_location">Share Location</MenuItem>
                                            <MenuItem value="calendar_event">Calendar Event</MenuItem>
                                          </Select>
                                        </FormControl>
                                        <IconButton size="small" onClick={() => removeCardSuggestion(cardIndex, sIndex)} sx={{ color: ColorsConstants.error, flexShrink: 0 }}>
                                          <CloseIcon fontSize="small" />
                                        </IconButton>
                                      </Stack>
                                      <Stack spacing={1}>
                                        <TextField fullWidth size="small" label="Button Label" placeholder="e.g. Buy Now"
                                          value={suggestion.text || suggestion.displayText || ""}
                                          onChange={(e) => updateCardSuggestion(cardIndex, sIndex, "text", e.target.value)}
                                          error={!!errors[`card_${cardIndex}_sug_${sIndex}_text`]} helperText={errors[`card_${cardIndex}_sug_${sIndex}_text`]} sx={INPUT_SX} />
                                        <TextField fullWidth size="small" label="Postback ID" placeholder="hidden_id_123"
                                          value={suggestion.postback_data || ""}
                                          onChange={(e) => updateCardSuggestion(cardIndex, sIndex, "postback_data", e.target.value)} sx={INPUT_SX} />
                                        {/* Dynamic action fields for carousel suggestion */}
                                        {suggestion.action_type === "url_action" && <TextField fullWidth size="small" label="Website URL" placeholder="https://..." value={suggestion.url || ""} onChange={(e) => updateCardSuggestion(cardIndex, sIndex, "url", e.target.value)} error={!!errors[`card_${cardIndex}_sug_${sIndex}_url`]} sx={INPUT_SX} />}
                                        {suggestion.action_type === "dialer_action" && <TextField fullWidth size="small" label="Phone Number" placeholder="+91..." value={suggestion.phoneNumber || ""} onChange={(e) => updateCardSuggestion(cardIndex, sIndex, "phoneNumber", e.target.value)} error={!!errors[`card_${cardIndex}_sug_${sIndex}_phone`]} sx={INPUT_SX} />}
                                        {suggestion.action_type === "view_location_latlong" && (
                                          <Grid container spacing={1}>
                                            <Grid item xs={6}><TextField fullWidth size="small" label="Lat" type="number" value={suggestion.latitude || ""} onChange={(e) => updateCardSuggestion(cardIndex, sIndex, "latitude", parseFloat(e.target.value))} error={!!errors[`card_${cardIndex}_sug_${sIndex}_lat`]} helperText={errors[`card_${cardIndex}_sug_${sIndex}_lat`]} sx={INPUT_SX} /></Grid>
                                            <Grid item xs={6}><TextField fullWidth size="small" label="Long" type="number" value={suggestion.longitude || ""} onChange={(e) => updateCardSuggestion(cardIndex, sIndex, "longitude", parseFloat(e.target.value))} error={!!errors[`card_${cardIndex}_sug_${sIndex}_lng`]} helperText={errors[`card_${cardIndex}_sug_${sIndex}_lng`]} sx={INPUT_SX} /></Grid>
                                            <Grid item xs={12}><TextField fullWidth size="small" label="Pin Label" placeholder="e.g. Our Office" value={suggestion.label || ""} onChange={(e) => updateCardSuggestion(cardIndex, sIndex, "label", e.target.value)} sx={INPUT_SX} /></Grid>
                                          </Grid>
                                        )}
                                        {suggestion.action_type === "view_location_query" && <TextField fullWidth size="small" label="Search Query" placeholder="Pizza near me" value={suggestion.query || ""} onChange={(e) => updateCardSuggestion(cardIndex, sIndex, "query", e.target.value)} error={!!errors[`card_${cardIndex}_sug_${sIndex}_query`]} helperText={errors[`card_${cardIndex}_sug_${sIndex}_query`]} sx={INPUT_SX} />}
                                        {suggestion.action_type === "calendar_event" && (
                                          <Stack spacing={1} sx={{ p: 1, bgcolor: ColorsConstants.primaryBlue60, borderRadius: "6px" }}>
                                            <TextField fullWidth size="small" label="Event Title" value={suggestion.title || ""} onChange={(e) => updateCardSuggestion(cardIndex, sIndex, "title", e.target.value)} sx={INPUT_SX} />
                                            <TextField fullWidth size="small" multiline rows={2} label="Description" value={suggestion.description || ""} onChange={(e) => updateCardSuggestion(cardIndex, sIndex, "description", e.target.value)} sx={MULTILINE_SX} />
                                            <Stack direction="row" spacing={1}>
                                              <TextField fullWidth size="small" label="Start" type="datetime-local" InputLabelProps={{ shrink: true }} value={suggestion.start_time || ""} onChange={(e) => updateCardSuggestion(cardIndex, sIndex, "start_time", e.target.value)} sx={INPUT_SX} />
                                              <TextField fullWidth size="small" label="End" type="datetime-local" InputLabelProps={{ shrink: true }} value={suggestion.end_time || ""} onChange={(e) => updateCardSuggestion(cardIndex, sIndex, "end_time", e.target.value)} sx={INPUT_SX} />
                                            </Stack>
                                          </Stack>
                                        )}
                                        {suggestion.action_type === "share_location" && <Alert severity="info" sx={{ py: 0, ...typography.bodySmall }}>User will be prompted to share location.</Alert>}
                                      </Stack>
                                    </Box>
                                  ))}
                                </Stack>
                              </Box>
                            </Grid>
                          </Grid>
                        </Box>
                      ))}
                    </Stack>
                  </Box>
                )}
              </Box>

              {/* ── Section 3: Action Buttons / Suggestions ───────────── */}
              {formData.template_type !== "carousel" && (
                <Box sx={{ ...CARD_SX, p: 3 }}>
                  <Box sx={{ ...SECTION_HEADER_SX, justifyContent: "space-between" }}>
                    <Stack direction="row" spacing={1} alignItems="center">
                      <ReplyIcon sx={{ color: ColorsConstants.primaryBlue100, fontSize: 18 }} />
                      <Typography sx={{ ...typography.bodyLarge, fontWeight: 700, color: ColorsConstants.primaryBlue100 }}>
                        {isCard ? "Action Buttons" : "Suggested Reply Chips"}
                      </Typography>
                    </Stack>
                    <ButtonThree
                      startIcon={<AddIcon sx={{ fontSize: "14px !important" }} />}
                      onClick={addSuggestion}
                      disabled={formData.suggestions.length >= maxSuggestions}
                      sx={{ height: 32, ...typography.bodySmall }}
                    >
                      Add {isCard ? "Button" : "Chip"} ({formData.suggestions.length}/{maxSuggestions})
                    </ButtonThree>
                  </Box>

                  {errors.suggestions && <Alert severity="error" sx={{ mb: 2, ...typography.bodySmall }}>{errors.suggestions}</Alert>}

                  {formData.suggestions.length === 0 && (
                    <Alert severity="info" sx={{ ...typography.bodySmall }}>
                      {isCard ? "Add up to 4 vertical action buttons." : "Add up to 11 horizontal reply chips."}
                    </Alert>
                  )}

                  <Stack spacing={2} sx={{ mt: formData.suggestions.length > 0 ? 2 : 0 }}>
                    {formData.suggestions.map((suggestion, index) => (
                      <Box key={index} sx={{ p: 2, backgroundColor: ColorsConstants.primaryWhite, border: `1px solid ${borderColors.cardBorderPrimaryGrayColor}`, borderLeft: `4px solid ${ColorsConstants.primaryBlue100}`, borderRadius: "10px" }}>
                        <Grid container spacing={2}>
                          <Grid item xs={12} sm={4}>
                            <DSSelect label="Type of Action" value={suggestion.action_type || suggestion.suggestionType || "reply"} onChange={(e) => updateSuggestion(index, "action_type", e.target.value)} size="small">
                              <MenuItem value="reply">Reply (Postback)</MenuItem>
                              <MenuItem value="url_action">Open URL</MenuItem>
                              <MenuItem value="dialer_action">Dial Phone</MenuItem>
                              <MenuItem value="view_location_latlong">View Location (Lat/Long)</MenuItem>
                              <MenuItem value="view_location_query">View Location (Query)</MenuItem>
                              <MenuItem value="share_location">Share Location</MenuItem>
                              <MenuItem value="calendar_event">Calendar Event</MenuItem>
                            </DSSelect>
                          </Grid>
                          <Grid item xs={12} sm={4}>
                            <TextField fullWidth size="small" label="Button Label"
                              value={suggestion.text || suggestion.displayText || ""}
                              onChange={(e) => updateSuggestion(index, "text", e.target.value)}
                              placeholder="e.g., Visit Website"
                              error={!!errors[`suggestion_${index}`]} helperText={errors[`suggestion_${index}`] || `${(suggestion.text || suggestion.displayText || "").length}/25`}
                              InputProps={{ endAdornment: <InputAdornment position="end"><CharCount current={(suggestion.text || suggestion.displayText || "").length} max={25} /></InputAdornment> }}
                              sx={INPUT_SX}
                            />
                          </Grid>
                          <Grid item xs={12} sm={4}>
                            <TextField fullWidth size="small" label="Postback ID"
                              value={suggestion.postback_data || suggestion.postback || ""}
                              onChange={(e) => updateSuggestion(index, "postback_data", e.target.value)}
                              placeholder="track_click_123"
                              sx={INPUT_SX}
                            />
                          </Grid>
                          {/* Dynamic fields */}
                          {(suggestion.action_type === "url_action" || suggestion.action_type === "dialer_action" || suggestion.action_type === "view_location_latlong" || suggestion.action_type === "view_location_query" || suggestion.action_type === "calendar_event" || suggestion.action_type === "share_location") && (
                            <Grid item xs={12}>
                              {renderActionFields(suggestion, index, updateSuggestion)}
                            </Grid>
                          )}
                          <Grid item xs={12} sx={{ textAlign: "right", pt: 0 }}>
                            <ButtonThree onClick={() => removeSuggestion(index)} startIcon={<DeleteIcon sx={{ fontSize: "14px !important" }} />}
                              sx={{ height: 30, color: ColorsConstants.error, borderColor: `${ColorsConstants.error}40`, ...typography.bodySmall }}>
                              Remove
                            </ButtonThree>
                          </Grid>
                        </Grid>
                      </Box>
                    ))}
                  </Stack>
                </Box>
              )}

            </Stack>
          </Grid>

          {/* ══════════════════════════════════════════════════════════════
              RIGHT: LIVE PREVIEW
          ══════════════════════════════════════════════════════════════ */}
          <Grid item xs={12} lg={4}>
            <Box sx={{ position: "sticky", top: 20 }}>
              <Box sx={{ ...CARD_SX, p: 2.5, mb: 2 }}>
                <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 2 }}>
                  <PreviewIcon sx={{ color: ColorsConstants.primaryBlue100, fontSize: 18 }} />
                  <Typography sx={{ ...typography.bodyLarge, fontWeight: 700, color: ColorsConstants.primaryBlue100 }}>Live Preview</Typography>
                </Stack>
                <Divider sx={{ borderColor: borderColors.cardBorderPrimaryGrayColor, mb: 2.5 }} />

                <PhoneMockup botName={bot?.bot_name} botLogo={bot?.bot_logo}>
                  {renderTemplatePreview()}
                </PhoneMockup>
              </Box>

              <Box sx={{ ...CARD_SX, p: 2.5 }}>
                <Typography sx={{ ...typography.bodyLarge, fontWeight: 700, color: ColorsConstants.primaryBlue100, mb: 1.5 }}>Summary</Typography>
                <Stack spacing={1}>
                  {[
                    { label: "Name",     value: formData.name || "—" },
                    { label: "Type",     value: currentConfig.label },
                    { label: "Use Case", value: formData.template_use_case },
                    { label: "Suggestions", value: `${formData.suggestions.length} configured` },
                  ].map(({ label, value }) => (
                    <Box key={label} sx={{ display: "flex", justifyContent: "space-between", py: "6px", borderBottom: `1px solid ${borderColors.cardBorderPrimaryGrayColor}` }}>
                      <Typography sx={{ ...typography.bodySmall, color: grayFontColor }}>{label}</Typography>
                      <Typography sx={{ ...typography.bodySmall, fontWeight: 600, color: ColorsConstants.primaryBlue100, maxWidth: 140, textAlign: "right", wordBreak: "break-word" }}>{value}</Typography>
                    </Box>
                  ))}
                </Stack>
              </Box>
            </Box>
          </Grid>

        </Grid>
      </Box>

      {/* ── Sticky Footer ───────────────────────────────────────────────── */}
      <Box sx={{ bottom: 0, left: 0, right: 0,
        backgroundColor: ColorsConstants.primaryWhite,
        borderTop: `1px solid ${borderColors.cardBorderPrimaryGrayColor}`,
        px: 4, py: 1.5, zIndex: 100,
        boxShadow: "0 -2px 12px rgba(8,36,92,0.07)",
      }}>
        <Box sx={{ maxWidth: 1440, mx: "auto", display: "flex", justifyContent: "space-between", alignItems: "center" }}>
          <ButtonOne variant="outlined" onClick={onBack} startIcon={<ArrowBackIcon sx={{ fontSize: "16px !important" }} />}>
            Cancel
          </ButtonOne>

          <Stack direction="row" spacing={1.5} alignItems="center">
            {loading && <CircularProgress size={18} sx={{ color: ColorsConstants.primaryBlue100 }} />}
            <ButtonOne variant="outlined" onClick={() => handleSave("DRAFT")} disabled={loading} startIcon={<SaveIcon sx={{ fontSize: "16px !important" }} />}>
              {loading ? "Saving..." : "Save as Draft"}
            </ButtonOne>
            <ButtonOne variant="contained" onClick={() => handleSave("SUBMITTED")} disabled={loading} startIcon={<SendIcon sx={{ fontSize: "16px !important" }} />}>
              {loading ? "Submitting..." : "Submit for Approval"}
            </ButtonOne>
          </Stack>
        </Box>
      </Box>

    </Box>
  );
};

export default RCSCreateTemplate;